import {
  Button,
  Card,
  Checkbox,
  DataStatus,
  IconButton,
  Radio,
  showNotification,
} from 'platform/components';
import {Box, Hide, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {equals, move, without} from 'ramda';

import {
  PromotionalPhotoFinancingDisplay,
  PromotionalPhotoFinancingDisplayMode,
  useGetPromotionalPhotoFinancingDisplayQuery,
  useSetPromotionalPhotoFinancingDisplayMutation,
} from '@dms/api/settings';
import {FinancialProductType} from '@dms/api/shared';
import {DragHandle, SortableCardList} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {getFinancialProductTypeLabel} from '../../../utils/getFinancialProductTypeLabel';
import {
  createFinancingDisplayDraft,
  FinancingDisplayDraft,
  getFinancingDisplayRequest,
} from '../utils/financingDisplayDraft';

const CONTAINER_ID = 'financing-display-order';

const MODE_OPTIONS: {value: PromotionalPhotoFinancingDisplayMode; label: string}[] = [
  {value: 'ALL', label: i18n.t('entity.promoPhotos.financingDisplay.labels.modeAll')},
  {value: 'PREFERRED', label: i18n.t('entity.promoPhotos.financingDisplay.labels.modePreferred')},
];

/**
 * Tenant setting deciding which financing promo cards a vehicle gets: every product with a valid
 * offer, or only the first one in a preferred order.
 */
export function FinancingDisplaySettings(props: RequiredTestIdProps) {
  const {data, isLoading, isError} = useGetPromotionalPhotoFinancingDisplayQuery();
  const [setDisplay, {isLoading: isSaving}] = useSetPromotionalPhotoFinancingDisplayMutation();

  return (
    <Card
      title={i18n.t('entity.promoPhotos.financingDisplay.labels.title')}
      data-testid={props['data-testid']}
    >
      <DataStatus isLoading={isLoading} isError={isError}>
        <Show when={data}>
          {(data) => (
            <FinancingDisplayForm
              saved={data}
              isSaving={isSaving}
              onSave={(value) =>
                setDisplay(value)
                  .unwrap()
                  .then(() =>
                    showNotification.success(
                      i18n.t('entity.promoPhotos.financingDisplay.notifications.saved')
                    )
                  )
                  .catch(handleApiError)
              }
              data-testid={suffixTestId('form', props)}
            />
          )}
        </Show>
      </DataStatus>
    </Card>
  );
}

interface FinancingDisplayFormProps extends RequiredTestIdProps {
  saved: PromotionalPhotoFinancingDisplay;
  isSaving: boolean;
  onSave: (value: PromotionalPhotoFinancingDisplay) => void;
}

// Keyed on the saved value by the parent (DataStatus remounts on refetch), so the draft is
// initialised from props once.
function FinancingDisplayForm(props: FinancingDisplayFormProps) {
  const [initialDraft] = useState(() => createFinancingDisplayDraft(props.saved));
  const [draft, setDraft] = useState<FinancingDisplayDraft>(initialDraft);
  const request = getFinancingDisplayRequest(draft);
  const isDirty = !equals(request, getFinancingDisplayRequest(initialDraft));
  const orderItems = draft.order.map((type) => ({id: type}));
  const {checkedProducts} = draft;

  const toggleProduct = (type: FinancialProductType, isChecked: boolean) =>
    setDraft({
      ...draft,
      checkedProducts: isChecked ? [...checkedProducts, type] : without([type], checkedProducts),
    });

  const moveProduct = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= draft.order.length) {
      return;
    }
    setDraft({...draft, order: move(fromIndex, toIndex, draft.order)});
  };

  return (
    <VStack spacing={4} align="flex-start">
      <Radio
        label={i18n.t('entity.promoPhotos.financingDisplay.labels.mode')}
        direction="column"
        options={MODE_OPTIONS}
        value={draft.mode}
        onChange={(mode) =>
          setDraft({...draft, mode: mode as PromotionalPhotoFinancingDisplayMode})
        }
        data-testid={suffixTestId('mode', props)}
      />
      <Hide when={draft.mode !== 'PREFERRED'}>
        <VStack spacing={2}>
          <Text size="small" color="secondary">
            {i18n.t('entity.promoPhotos.financingDisplay.labels.orderHint')}
          </Text>
          <Box width={100}>
            <SortableCardList<{id: FinancialProductType}>
              containerId={CONTAINER_ID}
              items={orderItems}
              withHandle
              disableDropAnimation
              onReorder={(items) => setDraft({...draft, order: items.map((item) => item.id)})}
              renderCard={(item, index, handleProps) => (
                <Box
                  padding={2}
                  borderRadius="small"
                  border="1px solid"
                  borderColor="general.separator"
                  backgroundColor="general.white"
                >
                  <HStack spacing={2} align="center">
                    <DragHandle
                      {...handleProps}
                      aria-label={i18n.t('general.actions.move')}
                      data-testid={suffixTestId(`handle-${item.id}`, props)}
                    >
                      <Icon value="action/drag_indicator" size={4} color="text.tertiary" />
                    </DragHandle>
                    <Checkbox
                      value={checkedProducts.includes(item.id)}
                      // At least one product stays in the PREFERRED order.
                      isDisabled={checkedProducts.length === 1 && checkedProducts.includes(item.id)}
                      onChange={(isChecked) => toggleProduct(item.id, isChecked)}
                      aria-label={getFinancialProductTypeLabel(item.id)}
                      data-testid={suffixTestId(`checkbox-${item.id}`, props)}
                    />
                    <Text size="small">{index + 1}.</Text>
                    <Box flex={1}>
                      <Text size="small" data-testid={suffixTestId(`product-${item.id}`, props)}>
                        {getFinancialProductTypeLabel(item.id)}
                      </Text>
                    </Box>
                    <IconButton
                      icon="navigation/arrow_upward"
                      size="small"
                      isDisabled={index === 0}
                      onClick={() => moveProduct(index, index - 1)}
                      data-testid={suffixTestId(`up-${item.id}`, props)}
                    />
                    <IconButton
                      icon="navigation/arrow_downward"
                      size="small"
                      isDisabled={index === draft.order.length - 1}
                      onClick={() => moveProduct(index, index + 1)}
                      data-testid={suffixTestId(`down-${item.id}`, props)}
                    />
                  </HStack>
                </Box>
              )}
            />
          </Box>
        </VStack>
      </Hide>
      <Button
        variant="primary"
        title={i18n.t('general.actions.save')}
        isDisabled={!isDirty}
        isLoading={props.isSaving}
        onClick={() => props.onSave(request)}
        data-testid={suffixTestId('save', props)}
      />
    </VStack>
  );
}
